import { Link } from 'react-router-dom';
import { MessageSquareDot, Shield, Zap, Ban } from 'lucide-react';

const InstagramCommentDMPage = () => {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">

        {/* ── English (for Meta Reviewer) ──────────────────────────────── */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquareDot size={32} className="text-pink-500 shrink-0" />
            <h1 className="text-3xl font-bold text-gray-900">
              Instagram Comment-to-DM — How it works
            </h1>
          </div>
          <p className="text-gray-500 mb-8 ml-11">
            Feature description for Meta App Review ·{' '}
            <a href="mailto:schuchardt@foerderfuchs-bildung.de" className="text-orange-600 hover:underline">
              schuchardt@foerderfuchs-bildung.de
            </a>
          </p>

          <p className="text-gray-700 mb-6">
            <strong>Förderfuchs Social Media Manager</strong> is a content-management tool
            developed by <strong>Förderfuchs Bildung &amp; Coaching</strong> (
            <a href="https://foerderfuchs-bildung.de" className="text-orange-600 hover:underline" target="_blank" rel="noreferrer">
              foerderfuchs-bildung.de
            </a>
            ). It allows connected Instagram Business accounts to manage their social media
            presence — including scheduling posts, analysing engagement, and the feature
            described here: automated private replies to comments.
          </p>

          <div className="space-y-5">
            {[
              {
                icon: <Zap size={20} className="text-blue-500" />,
                title: '1. Rule setup by the business owner',
                body: 'The business owner logs into the dashboard and creates a keyword rule: a trigger word or phrase, and the text of the private reply to send. Rules can be toggled active or inactive at any time.'
              },
              {
                icon: <MessageSquareDot size={20} className="text-pink-500" />,
                title: '2. Comment received via Instagram Webhook',
                body: 'When a user comments on one of the business\'s own Instagram posts, Meta delivers the comment event to our webhook endpoint (field: "comments"). We verify the request using X-Hub-Signature-256 before processing it.'
              },
              {
                icon: <Zap size={20} className="text-green-500" />,
                title: '3. Keyword matching and exactly one private reply',
                body: (
                  <>
                    If the comment text contains the configured keyword (case-insensitive substring
                    match), the app sends <strong>exactly one</strong> private reply to that
                    specific comment via the Instagram Messaging API:
                    <code className="block mt-2 bg-gray-100 rounded px-3 py-2 text-sm font-mono break-all">
                      POST https://graph.facebook.com/v18.0/&#123;ig-user-id&#125;/messages<br />
                      {'{"recipient": {"comment_id": "<id>"}, "message": {"text": "<reply>"}}'}<br />
                      Permission required: <strong>instagram_manage_messages</strong>
                    </code>
                    Each comment can trigger at most one reply (deduplication by comment ID). No
                    match → no message sent.
                  </>
                )
              },
              {
                icon: <Shield size={20} className="text-purple-500" />,
                title: '4. Lead tracking with pending consent',
                body: 'If a reply is sent successfully, the commenter\'s handle is stored as a lead with consent status "pending". No further messages are sent unless the user explicitly opts in through the conversation. All event data is deleted after 90 days.'
              },
              {
                icon: <Ban size={20} className="text-red-500" />,
                title: 'What this feature does NOT do',
                body: 'We never send unsolicited, bulk, or cold DMs. A private reply is only ever sent as a direct response to a comment the user themselves posted. Replies from the business\'s own account are skipped (own-comment guard). There is no retry logic that could cause duplicate messages.'
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-4 rounded-lg border border-gray-100 bg-gray-50">
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                  <div className="text-gray-600 text-sm leading-relaxed">{item.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-800 border border-blue-100">
            <strong>Support:</strong>{' '}
            <a href="mailto:schuchardt@foerderfuchs-bildung.de" className="underline">schuchardt@foerderfuchs-bildung.de</a>
            {' · '}
            <Link to="/datenschutz" className="underline">Privacy Policy</Link>
            {' · '}
            <Link to="/nutzungsbedingungen" className="underline">Terms of Use</Link>
          </div>
        </div>

        <hr className="border-gray-200 my-8" />

        {/* ── Deutsch ──────────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <MessageSquareDot size={28} className="text-pink-500 shrink-0" />
            <h2 className="text-2xl font-bold text-gray-900">
              Kommentar → DM — So funktioniert es
            </h2>
          </div>
          <p className="text-gray-500 mb-6 ml-10">Deutsche Fassung</p>

          <p className="text-gray-700 mb-6">
            Der <strong>Förderfuchs Social Media Manager</strong> ist ein Content-Management-Tool
            von Förderfuchs Bildung &amp; Coaching. Mit dem Feature „Kommentar → DM" kannst du
            automatisch auf Kommentare unter deinen Instagram-Beiträgen reagieren — ohne
            manuelle Überwachung.
          </p>

          <div className="space-y-4">
            {[
              {
                num: '1.',
                title: 'Du legst eine Regel an',
                body: 'Im Dashboard unter „Kommentar → DM" gibst du ein Keyword und den Antworttext ein. Die Regel lässt sich jederzeit aktivieren oder pausieren.'
              },
              {
                num: '2.',
                title: 'Instagram meldet den Kommentar',
                body: 'Sobald jemand einen Kommentar unter einem deiner Beiträge hinterlässt, leitet Instagram ihn per Webhook an unsere App weiter. Wir prüfen dabei die digitale Signatur, damit nur echte Meta-Anfragen verarbeitet werden.'
              },
              {
                num: '3.',
                title: 'Die App sendet genau eine private Antwort',
                body: 'Enthält der Kommentar dein Keyword, sendet die App über die Instagram Messaging API genau eine private Antwort — direkt als Reaktion auf diesen Kommentar. Kein Match, keine Nachricht. Jeder Kommentar kann höchstens eine Antwort auslösen.'
              },
              {
                num: '4.',
                title: 'Lead mit ausstehender Einwilligung',
                body: 'Wird die Antwort erfolgreich gesendet, speichern wir den Benutzernamen als Kontaktanfrage mit dem Einwilligungsstatus „ausstehend". Erst wenn die Person im Gespräch aktiv zustimmt, wird der Status auf „eingewilligt" gesetzt. Ereignisdaten werden nach 90 Tagen gelöscht.'
              }
            ].map((item) => (
              <div key={item.num} className="flex gap-4 p-4 rounded-lg border border-gray-100 bg-gray-50">
                <span className="text-pink-500 font-bold text-lg shrink-0 w-6">{item.num}</span>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-orange-50 rounded-lg text-sm text-orange-800 border border-orange-100">
            Fragen?{' '}
            <a href="mailto:schuchardt@foerderfuchs-bildung.de" className="underline font-medium">
              schuchardt@foerderfuchs-bildung.de
            </a>
            {' · '}
            <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>
            {' · '}
            <Link to="/nutzungsbedingungen" className="underline">Nutzungsbedingungen</Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InstagramCommentDMPage;
